/**
 * ProgressBar Atom
 * @layer atom
 * @package @plerity/ui-core
 */

import type { FC } from "react";
import { cn } from "../utils/cn";

export interface ProgressBarProps {
  /** Value 0-max */
  value: number;
  /** Max value, defaults to 100 */
  max?: number;
  /** Optional label rendered above the bar (left side) */
  label?: string;
  /** Optional displayed value text rendered above the bar (right side) */
  valueText?: string;
  /** Color variant — maps to Tailwind color classes matching the ui-core palette */
  variant?: "primary" | "success" | "warning" | "danger";
  /** Bar height */
  size?: "sm" | "md" | "lg";
  /** Additional class names applied to the outer wrapper */
  className?: string;
}

const trackHeight: Record<string, string> = {
  sm: "h-1",
  md: "h-2",
  lg: "h-3",
};

const fillColor: Record<string, string> = {
  primary: "bg-brand-500 dark:bg-brand-400",
  success: "bg-green-500 dark:bg-green-400",
  warning: "bg-yellow-500 dark:bg-yellow-400",
  danger: "bg-red-500 dark:bg-red-400",
};

export const ProgressBar: FC<ProgressBarProps> = ({
  value,
  max = 100,
  label,
  valueText,
  variant = "primary",
  size = "md",
  className,
}) => {
  const pct = Math.min(100, Math.max(0, max > 0 ? (value / max) * 100 : 0));

  return (
    <div className={cn("w-full", className)}>
      {(label || valueText) && (
        <div className="mb-1 flex items-center justify-between text-xs">
          {/* dark pair required: bare text-gray-500 was 3.07:1 on a slate-800 card (axe, ai 2026-10-12) */}
          {label && <span className="text-gray-500 dark:text-gray-400">{label}</span>}
          {valueText !== undefined && <span className="font-medium text-gray-900 dark:text-gray-100">{valueText}</span>}
        </div>
      )}
      <div
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={label ?? "Progress"}
        className={cn(
          // Literal gray ramp — do NOT use `bg-surface-subtle` here. It emits
          // `background-color: var(--color-bg-surface-subtle)` with NO fallback and
          // that var is stripped from the React SPA apps' Tailwind v4 build.
          "w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700",
          trackHeight[size]
        )}
      >
        <div
          className={cn("h-full rounded-full transition-all duration-300 ease-out", fillColor[variant])}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

ProgressBar.displayName = "ProgressBar";
